import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Plus, Users, Copy, Pencil, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, Avatar, EmptyState, LevelBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

export default function Classes() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [classes, setClasses] = useState([]);
  const [styles, setStyles] = useState([]);
  const [levels, setLevels] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [dancers, setDancers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = () => {
    api.get("/classes").then((r) => setClasses(r.data));
    api.get("/curriculum/styles").then((r) => setStyles(r.data));
    api.get("/curriculum/levels").then((r) => setLevels(r.data));
    api.get("/enrollments").then((r) => setEnrollments(r.data)).catch(() => {});
    if (isDirector || user?.role === "teacher") {
      api.get("/people/teachers").then((r) => setTeachers(r.data)).catch(() => {});
      api.get("/people/dancers").then((r) => setDancers(r.data)).catch(() => {});
    }
  };
  useEffect(load, []);

  const styleMap = Object.fromEntries(styles.map((s) => [s.id, s]));
  const levelMap = Object.fromEntries(levels.map((l) => [l.id, l]));
  const teacherMap = Object.fromEntries(teachers.map((t) => [t.id, t]));
  const dancerMap = Object.fromEntries(dancers.map((d) => [d.id, d]));

  const empty = { name: "", style_id: "", level_id: "", age_range: "", day: "", start_time: "", end_time: "", room: "", teacher_ids: [], capacity: 20, active: true };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const save = async () => {
    try {
      if (editId) await api.put(`/classes/${editId}`, form); else await api.post("/classes", form);
      toast.success("Class saved"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Error"); }
  };
  const duplicate = async (id) => { await api.post(`/classes/${id}/duplicate`); toast.success("Class duplicated"); load(); };
  const to12 = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); if (isNaN(h)) return t; const ap = h >= 12 ? "PM" : "AM"; const hh = ((h + 11) % 12) + 1; return `${hh}:${String(m || 0).padStart(2, "0")} ${ap}`; };

  return (
    <div className="stagger-in">
      <PageHeader title={isDirector ? "Classes" : "My Classes"} subtitle="Schedules, rosters and enrollments."
        testid="classes-page"
        action={isDirector && <Button data-testid="add-class-button" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Add Class</Button>} />

      {classes.length === 0 ? <EmptyState icon={CalendarDays} title="No classes yet" hint="Create your first class to get started." /> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {classes.map((cl) => {
            const enr = enrollments.filter((e) => e.class_id === cl.id && e.status !== "Withdrawn");
            return (
              <div key={cl.id} data-testid={`class-card-${cl.id}`} className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-head font-bold text-lg">{cl.name}</p>
                    <div className="mt-1"><LevelBadge name={levelMap[cl.level_id]?.name} color={levelMap[cl.level_id]?.color} small /></div>
                  </div>
                  <Badge variant="outline" className="rounded-full">{styleMap[cl.style_id]?.name || "—"}</Badge>
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2"><Clock className="w-4 h-4" />{cl.day} · {to12(cl.start_time)}–{to12(cl.end_time)}</p>
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4" />{cl.room} · ages {cl.age_range}</p>
                  <p className="flex items-center gap-2"><Users className="w-4 h-4" />{enr.length}/{cl.capacity} enrolled</p>
                </div>
                <div className="flex -space-x-2 mt-3">
                  {(cl.teacher_ids || []).map((tid) => <Avatar key={tid} first={teacherMap[tid]?.first_name} last={teacherMap[tid]?.last_name} size={30} color="#000" />)}
                  {(cl.teacher_ids || []).length === 0 && <span className="text-xs text-muted-foreground">No teacher assigned</span>}
                </div>
                <div className="flex gap-2 mt-4">
                  {(isDirector || user?.role === "teacher") && (
                    <Button size="sm" variant="outline" className="rounded-full flex-1" data-testid={`view-roster-${cl.id}`} onClick={() => setDetail(cl)}>Roster</Button>
                  )}
                  {isDirector && <>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`edit-class-${cl.id}`} onClick={() => { setForm({ ...empty, ...cl }); setEditId(cl.id); setOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`duplicate-class-${cl.id}`} onClick={() => duplicate(cl.id)}><Copy className="w-3.5 h-3.5" /></Button>
                  </>}
                </div>
              </div>
            );
          })}
        </div>}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Edit" : "Add"} Class</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="class-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Style</Label><select data-testid="class-style" className="w-full border rounded-md h-10 px-3" value={form.style_id} onChange={(e) => setForm({ ...form, style_id: e.target.value, level_id: "" })}><option value="">—</option>{styles.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              <div className="space-y-1.5"><Label>Level</Label><select className="w-full border rounded-md h-10 px-3" value={form.level_id} onChange={(e) => setForm({ ...form, level_id: e.target.value })}><option value="">—</option>{levels.filter((l) => l.style_id === form.style_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
              <div className="space-y-1.5"><Label>Day</Label><Input value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Room</Label><Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Start</Label><Input data-testid="class-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End</Label><Input data-testid="class-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Age range</Label><Input value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })} placeholder="8-11" /></div>
              <div className="space-y-1.5"><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Assign teachers</Label><div className="flex flex-wrap gap-2">{teachers.map((t) => { const on = form.teacher_ids?.includes(t.id); return <button key={t.id} type="button" onClick={() => setForm({ ...form, teacher_ids: on ? form.teacher_ids.filter((x) => x !== t.id) : [...(form.teacher_ids || []), t.id] })} className={`px-3 py-1 rounded-full text-sm border ${on ? "bg-teal text-white border-teal" : "bg-white"}`}>{t.preferred_name || t.first_name}</button>; })}</div></div>
          </div>
          <DialogFooter><Button data-testid="save-class-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save Class</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Roster dialog */}
      <RosterDialog cls={detail} onClose={() => setDetail(null)} enrollments={enrollments} dancerMap={dancerMap}
        allDancers={dancers} isDirector={isDirector} reload={load} />
    </div>
  );
}

function RosterDialog({ cls, onClose, enrollments, dancerMap, allDancers, isDirector, reload }) {
  const [addId, setAddId] = useState("");
  if (!cls) return null;
  const roster = enrollments.filter((e) => e.class_id === cls.id && e.status !== "Withdrawn");
  const enroll = async () => {
    if (!addId) return;
    try { await api.post("/enrollments", { dancer_id: addId, class_id: cls.id }); toast.success("Dancer enrolled"); setAddId(""); reload(); }
    catch (e) { toast.error("Error"); }
  };
  const remove = async (eid) => { await api.delete(`/enrollments/${eid}`); toast.success("Dancer withdrawn (history kept)"); reload(); };
  const available = allDancers.filter((d) => !roster.some((r) => r.dancer_id === d.id));
  return (
    <Dialog open={!!cls} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">{cls.name} — Roster</DialogTitle></DialogHeader>
        {isDirector && (
          <div className="flex gap-2">
            <select data-testid="enroll-dancer-select" className="flex-1 border rounded-md h-10 px-3" value={addId} onChange={(e) => setAddId(e.target.value)}>
              <option value="">Select dancer to enroll…</option>
              {available.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
            </select>
            <Button data-testid="enroll-dancer-button" onClick={enroll} className="rounded-full bg-teal hover:bg-teal-dark"><UserPlus className="w-4 h-4" /></Button>
          </div>
        )}
        <div className="space-y-2 mt-2">
          {roster.length === 0 && <p className="text-sm text-muted-foreground">No dancers enrolled yet.</p>}
          {roster.map((e) => {
            const d = dancerMap[e.dancer_id];
            return (
              <div key={e.id} data-testid={`roster-row-${e.dancer_id}`} className="flex items-center justify-between bg-background rounded-xl px-4 py-2.5">
                <div className="flex items-center gap-3"><Avatar first={d?.first_name} last={d?.last_name} size={34} />
                  <span className="font-semibold text-sm">{d ? `${d.first_name} ${d.last_name}` : "Dancer"}</span></div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="rounded-full">{e.status}</Badge>
                  {isDirector && <button data-testid={`remove-enroll-${e.dancer_id}`} onClick={() => remove(e.id)} className="text-xs text-red-500 font-semibold">Remove</button>}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
